import React, {
  createContext,
  useContext,
  useState,
  useEffect,
  ReactNode,
} from "react";
import { Project, ProjectState } from "../types";
import { ProjectAPI } from "../api/project";

// ローカルストレージのキー
const SELECTED_PROJECT_KEY = "selectedProjectId";

interface ProjectContextType extends ProjectState {
  setCurrentProject: (project: Project | null) => void;
  loadProjects: () => Promise<void>;
  createProject: (name: string, description?: string) => Promise<void>;
  updateProject: (id: string, updates: Partial<Project>) => Promise<void>;
  deleteProject: (id: string) => Promise<void>;
}

const ProjectContext = createContext<ProjectContextType | undefined>(undefined);

export const useProject = () => {
  const context = useContext(ProjectContext);
  if (!context) {
    throw new Error("useProject must be used within a ProjectProvider");
  }
  return context;
};

interface ProjectProviderProps {
  children: ReactNode;
}

export const ProjectProvider: React.FC<ProjectProviderProps> = ({
  children,
}) => {
  const [projectState, setProjectState] = useState<ProjectState>({
    currentProject: null,
    projects: [],
    isLoading: false,
    error: null,
  });

  const setCurrentProject = (project: Project | null) => {
    setProjectState((prev) => ({ ...prev, currentProject: project }));

    // ローカルストレージに保存
    if (project) {
      localStorage.setItem(SELECTED_PROJECT_KEY, project.id);
    } else {
      localStorage.removeItem(SELECTED_PROJECT_KEY);
    }
  };

  const loadProjects = async () => {
    console.log(`🔄 [${new Date().toISOString()}] loadProjects() called - stack:`, new Error().stack);
    try {
      setProjectState((prev) => ({ ...prev, isLoading: true, error: null }));

      console.log(`🚀 [${new Date().toISOString()}] Calling ProjectAPI.getAllProjects()...`);
      const response = await ProjectAPI.getAllProjects();
      console.log(`✅ [${new Date().toISOString()}] ProjectAPI.getAllProjects() returned`);
      console.log("📋 Projects API response:", response);

      if (response.success && response.data) {
        // Now ProjectAPI.getAllProjects() returns the converted format
        const projects = response.data.projects || [];
        console.log("📂 Projects loaded:", projects);

        // 保存されたプロジェクトIDを取得
        const savedProjectId = localStorage.getItem(SELECTED_PROJECT_KEY);
        let currentProject: Project | null = null;

        // 保存されたプロジェクトIDに対応するプロジェクトを検索
        if (savedProjectId) {
          currentProject =
            projects.find((p) => p.id === savedProjectId) || null;
          console.log("🔄 Restored project from localStorage:", currentProject);
        }

        setProjectState((prev) => ({
          ...prev,
          projects: projects,
          currentProject: currentProject,
          isLoading: false,
        }));
      } else {
        throw new Error(response.error || "Failed to load projects");
      }
    } catch (error) {
      console.error("❌ Failed to load projects:", error);
      setProjectState((prev) => ({
        ...prev,
        isLoading: false,
        error:
          error instanceof Error ? error.message : "Failed to load projects",
      }));
    }
  };

  const createProject = async (name: string, description?: string) => {
    try {
      setProjectState((prev) => ({ ...prev, isLoading: true, error: null }));

      // TODO: Implement actual API call
      const newProject: Project = {
        id: Date.now().toString(),
        name,
        description,
      };

      setProjectState((prev) => ({
        ...prev,
        projects: [...prev.projects, newProject],
        isLoading: false,
      }));
    } catch (error) {
      setProjectState((prev) => ({
        ...prev,
        isLoading: false,
        error:
          error instanceof Error ? error.message : "Failed to create project",
      }));
    }
  };

  const updateProject = async (id: string, updates: Partial<Project>) => {
    try {
      setProjectState((prev) => ({ ...prev, isLoading: true, error: null }));

      // TODO: Implement actual API call
      setProjectState((prev) => ({
        ...prev,
        projects: prev.projects.map((project) =>
          project.id === id ? { ...project, ...updates } : project
        ),
        currentProject:
          prev.currentProject?.id === id
            ? { ...prev.currentProject, ...updates }
            : prev.currentProject,
        isLoading: false,
      }));
    } catch (error) {
      setProjectState((prev) => ({
        ...prev,
        isLoading: false,
        error:
          error instanceof Error ? error.message : "Failed to update project",
      }));
    }
  };

  const deleteProject = async (id: string) => {
    try {
      setProjectState((prev) => ({ ...prev, isLoading: true, error: null }));

      // TODO: Implement actual API call
      setProjectState((prev) => {
        const isCurrentProject = prev.currentProject?.id === id;

        // 削除されるプロジェクトが現在選択されている場合、ローカルストレージもクリア
        if (isCurrentProject) {
          localStorage.removeItem(SELECTED_PROJECT_KEY);
        }

        return {
          ...prev,
          projects: prev.projects.filter((project) => project.id !== id),
          currentProject: isCurrentProject ? null : prev.currentProject,
          isLoading: false,
        };
      });
    } catch (error) {
      setProjectState((prev) => ({
        ...prev,
        isLoading: false,
        error:
          error instanceof Error ? error.message : "Failed to delete project",
      }));
    }
  };

  useEffect(() => {
    console.log(`📌 [${new Date().toISOString()}] ProjectContext useEffect triggered`);
    loadProjects();
  }, []);

  const value: ProjectContextType = {
    ...projectState,
    setCurrentProject,
    loadProjects,
    createProject,
    updateProject,
    deleteProject,
  };

  return (
    <ProjectContext.Provider value={value}>{children}</ProjectContext.Provider>
  );
};
